import { useCallback } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { githubPullRequestsApiRef } from '@roadiehq/backstage-plugin-github-pull-requests';
import { scmAuthApiRef } from '@backstage/integration-react';
import { deploymentsApiRef } from '@internal/plugin-deployments';
import {
  dependencySecurityApiRef,
  type DependencySecurityApi,
} from '@internal/plugin-dependency-security';
import { lockManagerApiRef } from '@internal/plugin-lock-manager';
import {
  DEVBOX_PATHS,
  devboxManagerApiRef,
} from '@internal/backstage-plugin-devbox-manager';
import {
  cloudflareWorkersApiRef,
  getVibeCodedAppPath,
} from '@internal/plugin-cloudflare-workers';
import { searchPullRequests } from '../searchPullRequests';
import { GITHUB_ORG } from '../githubOrg';
import { type AsyncValue, useAsyncValue } from '../useAsyncValue';
import { deployItems, fetchMergedPullRequests } from './myDeploys';
import {
  fetchMyPullRequests,
  pullRequestItem,
  type MyPullRequest,
} from './myPullRequests';
import { DAY_MS, HOUR_MS, ageMs, timeAgo } from './time';
import { NeedsYouItem, NeedsYouSourceState } from './types';

// Each source is a hook that every render calls (hooks can't be skipped), so a
// disabled source short-circuits to an empty list without a request.

const toState = (
  enabled: boolean,
  value: { loading: boolean; value?: NeedsYouItem[]; error?: Error },
): NeedsYouSourceState =>
  enabled
    ? { items: value.value ?? [], loading: value.loading, error: value.error }
    : { items: [], loading: false };

const none = async (): Promise<NeedsYouItem[]> => [];

export const useCurrentUserRef = () => {
  const identityApi = useApi(identityApiRef);
  const load = useCallback(
    () =>
      identityApi
        .getBackstageIdentity()
        .then(identity => identity.userEntityRef),
    [identityApi],
  );
  return useAsyncValue(load).value;
};

// ---- GitHub ----------------------------------------------------------------

export const repoName = (repositoryUrl: string) =>
  new URL(repositoryUrl).pathname
    .split('/')
    .filter(Boolean)
    .slice(1, 3)
    .join('/');

/** Pull requests where the user's review is requested by name. Needs a
 * GitHub sign-in. */
export const useReviewRequestItems = (
  enabled: boolean,
  signedIn: boolean,
): NeedsYouSourceState => {
  const api = useApi(githubPullRequestsApiRef);
  const active = enabled && signedIn;
  const load = useCallback(async () => {
    // Only reviews requested from you by name: `review-requested` also
    // matches every team you're in, which runs into hundreds.
    const requested = await searchPullRequests(
      api,
      `is:open is:pr user-review-requested:@me archived:false org:${GITHUB_ORG}`,
    );
    // Roadie returns `created_at` already as relative text ("2 days ago")
    // and no ISO date, so review requests have no `since` for tie-breaking.
    const opened = (pr: (typeof requested)[number]) =>
      pr.pullRequest.created_at ? `opened ${pr.pullRequest.created_at}` : '';
    return requested.map((pr): NeedsYouItem => ({
      id: `github-review-${pr.id}`,
      source: 'github-reviews',
      // Someone is blocked until you review.
      priority: 'blocking',
      urgency: 'warning',
      label: 'Review requested',
      meta: [
        `${repoName(pr.repositoryUrl)} #${pr.number}`,
        `by ${pr.user.login}`,
        opened(pr),
      ]
        .filter(Boolean)
        .join(' · '),
      title: pr.title,
      action: {
        label: 'Review',
        href: pr.pullRequest.htmlUrl ?? pr.htmlUrl,
        external: true,
      },
    }));
  }, [api]);
  return toState(active, useAsyncValue(active ? load : none));
};

// Not asked yet: no value at all, so a first load shows skeletons rather
// than an empty list.
const notAsked = async (): Promise<MyPullRequest[] | undefined> => undefined;

/**
 * The user's open pull requests with their review, merge and check state,
 * fetched once for both tabs of the card: Needs you takes the ones where
 * it's the user's move, the "Open pull requests" tab lists them all. Nothing
 * is asked until `active`.
 */
export const useMyPullRequests = (active: boolean) => {
  const scmAuth = useApi(scmAuthApiRef);
  const load = useCallback(() => fetchMyPullRequests(scmAuth), [scmAuth]);
  return useAsyncValue<MyPullRequest[] | undefined>(active ? load : notAsked);
};

/** The Needs you rows of the user's own pull requests (see pullRequestItem). */
export const myPullRequestItems = (
  enabled: boolean,
  prs: AsyncValue<MyPullRequest[] | undefined>,
): NeedsYouSourceState => {
  if (!enabled) return { items: [], loading: false };
  const now = Date.now();
  return {
    items: (prs.value ?? []).flatMap(pr => pullRequestItem(pr, now) ?? []),
    // The render that turns the fetch on comes before it starts.
    loading: prs.loading || (!prs.value && !prs.error),
    error: prs.error,
  };
};

// ---- Dependency security ---------------------------------------------------

type Finding = Awaited<
  ReturnType<DependencySecurityApi['getFindings']>
>['items'][number];

/** How many findings get a row of their own; the rest share one row. */
export const SECURITY_ROWS = 3;
const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1 };
const IMPACT_RANK: Record<string, number> = {
  affected: 0,
  'likely-affected': 1,
};

const impactRank = (finding: Finding): number | undefined =>
  IMPACT_RANK[finding.review?.impact ?? ''];

/** Critical or high, in runtime code, and either known to reach the
 * service or one upgrade away from fixed. */
const isActionable = (finding: Finding) =>
  finding.severity in SEVERITY_RANK &&
  finding.scope !== 'development' &&
  !finding.disposition &&
  (impactRank(finding) !== undefined || finding.fix.status === 'ready');

const rank = (finding: Finding) =>
  SEVERITY_RANK[finding.severity] * 10 +
  (impactRank(finding) ?? 2) * 2 +
  (finding.fix.status === 'ready' ? 0 : 1);

const findingItem = (finding: Finding): NeedsYouItem => {
  const fixReady = finding.fix.status === 'ready';
  const target = finding.fix.targetVersions[0];
  const impact = finding.review?.impact;
  return {
    id: `dependency-security-${finding.id}`,
    source: 'dependency-security',
    priority: finding.severity === 'critical' ? 'blocking' : 'your-move',
    urgency: finding.severity === 'critical' ? 'critical' : 'warning',
    label: `${finding.severity} vulnerability`,
    meta: [
      finding.repository,
      impactRank(finding) !== undefined ? impact?.replace('-', ' ') : undefined,
      fixReady ? 'fix ready' : undefined,
      `found ${timeAgo(finding.firstDetectedAt)}`,
    ]
      .filter(Boolean)
      .join(' · '),
    title:
      fixReady && finding.fix.kind === 'upgrade' && target
        ? `Upgrade ${finding.packageName} to ${target}`
        : `${finding.packageName} ${finding.installedVersions[0] ?? ''}`.trim(),
    action: {
      label: 'Details',
      href: `/dependency-security/findings/${encodeURIComponent(finding.id)}`,
    },
    since: new Date(finding.firstDetectedAt).getTime(),
  };
};

/**
 * A few security findings at a time. Services carry hundreds of findings, so
 * only the actionable ones get a row: at most SECURITY_ROWS, most severe and
 * surest first. One low-urgency row counts the other critical and high
 * findings and links to the findings page, which defaults to "my findings".
 */
export const useDependencySecurityItems = (
  enabled: boolean,
): NeedsYouSourceState => {
  const api = useApi(dependencySecurityApiRef);
  const load = useCallback(async () => {
    const mine = { myFindings: true, sort: 'priority', limit: 10 } as const;
    const [affected, likely, fixable, critical, high] = await Promise.all([
      api.getFindings({ ...mine, impact: 'affected' }),
      api.getFindings({ ...mine, impact: 'likely-affected' }),
      api.getFindings({ ...mine, fixStatus: 'ready' }),
      api.getFindings({ myFindings: true, severity: 'critical', limit: 1 }),
      api.getFindings({ myFindings: true, severity: 'high', limit: 1 }),
    ]);
    const byId = new Map<string, Finding>();
    for (const finding of [
      ...affected.items,
      ...likely.items,
      ...fixable.items,
    ]) {
      if (isActionable(finding) && !byId.has(finding.id)) {
        byId.set(finding.id, finding);
      }
    }
    // Array sort is stable, so the service's priority order breaks ties.
    const shown = [...byId.values()]
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, SECURITY_ROWS);
    const rest = critical.total + high.total - shown.length;
    const items = shown.map(findingItem);
    if (rest > 0) {
      items.push({
        id: 'dependency-security-rest',
        source: 'dependency-security',
        priority: 'background',
        urgency: 'low',
        label: 'Security findings',
        title: `${rest} ${shown.length > 0 ? 'more ' : ''}critical and high ${
          rest === 1 ? 'finding' : 'findings'
        } in services you own`,
        action: { label: 'Triage', href: '/dependency-security' },
      });
    }
    return items;
  }, [api]);
  return toState(enabled, useAsyncValue(enabled ? load : none));
};

// ---- Blueprints ------------------------------------------------------------

/** The user's Blueprint runs that failed in the last week. */
export const useBlueprintItems = (enabled: boolean): NeedsYouSourceState => {
  const api = useApi(scaffolderApiRef);
  const load = useCallback(async () => {
    const { tasks } = await api.listTasks({
      filterByOwnership: 'owned',
      limit: 50,
    });
    return tasks
      .filter(
        task => task.status === 'failed' && ageMs(task.createdAt) < 7 * DAY_MS,
      )
      .map((task): NeedsYouItem => ({
        id: `blueprint-${task.id}`,
        source: 'blueprints',
        priority: 'your-move',
        urgency: 'normal',
        label: 'Blueprint run failed',
        meta: timeAgo(task.createdAt),
        title:
          task.spec.templateInfo?.entity?.metadata?.title ??
          task.spec.templateInfo?.entityRef ??
          'Blueprint',
        action: { label: 'Open', href: `/create/tasks/${task.id}` },
        since: new Date(task.createdAt).getTime(),
      }));
  }, [api]);
  return toState(enabled, useAsyncValue(enabled ? load : none));
};

// ---- Locks -----------------------------------------------------------------

/** Locks the user holds. Holding one blocks others, so they rank high. */
export const useLockItems = (
  enabled: boolean,
  userRef: string | undefined,
): NeedsYouSourceState => {
  const api = useApi(lockManagerApiRef);
  const active = enabled && Boolean(userRef);
  const load = useCallback(async () => {
    const locks = await api.getLocks();
    return locks
      .filter(lock => lock.locked && lock.locked_by === userRef)
      .map((lock): NeedsYouItem => ({
        id: `lock-${lock.name}`,
        source: 'locks',
        // A lock held for hours blocks others; a fresh one is a reminder.
        ...(ageMs(lock.locked_at) > 4 * HOUR_MS
          ? { priority: 'blocking' as const, urgency: 'warning' as const }
          : { priority: 'wrap-up' as const, urgency: 'normal' as const }),
        label: 'You hold a lock',
        meta: lock.locked_at ? `locked ${timeAgo(lock.locked_at)}` : undefined,
        title: lock.name,
        detail: lock.comment,
        action: { label: 'Open', href: '/lock-manager' },
        since: lock.locked_at ? new Date(lock.locked_at).getTime() : undefined,
      }));
  }, [api, userRef]);
  return toState(active, useAsyncValue(active ? load : none));
};

// ---- Devboxes --------------------------------------------------------------

/** The user's devboxes that failed, or that haven't sent a heartbeat for a
 * day while still running. */
export const useDevboxItems = (
  enabled: boolean,
  userRef: string | undefined,
): NeedsYouSourceState => {
  const api = useApi(devboxManagerApiRef);
  const active = enabled && Boolean(userRef);
  const load = useCallback(async () => {
    const devboxes = await api.listDevboxes();
    return devboxes
      .filter(devbox => devbox.claimedBy?.backstageSubject === userRef)
      .flatMap((devbox): NeedsYouItem[] => {
        if (devbox.status === 'failed') {
          return [
            {
              id: `devbox-${devbox.id}`,
              source: 'devboxes',
              priority: 'your-move',
              urgency: 'warning',
              label: 'Devbox failed',
              meta: timeAgo(devbox.updatedAt),
              title: devbox.name,
              action: { label: 'Open', href: DEVBOX_PATHS.root },
            },
          ];
        }
        const running = ['ready', 'claimed'].includes(devbox.status);
        if (running && ageMs(devbox.lastHeartbeatAt) > DAY_MS) {
          return [
            {
              id: `devbox-${devbox.id}`,
              source: 'devboxes',
              priority: 'background',
              urgency: 'low',
              label: 'Devbox idle',
              meta: `last heartbeat ${timeAgo(devbox.lastHeartbeatAt ?? undefined)}`,
              title: devbox.name,
              detail: 'Pause it if you no longer need it running.',
              action: { label: 'Open', href: DEVBOX_PATHS.root },
            },
          ];
        }
        return [];
      });
  }, [api, userRef]);
  return toState(active, useAsyncValue(active ? load : none));
};

// ---- Internal apps ---------------------------------------------------------

/** Pending updates of the user's internal apps: failing checks, or ready to
 * publish. The endpoint is already scoped to the user. */
export const useInternalAppItems = (enabled: boolean): NeedsYouSourceState => {
  const api = useApi(cloudflareWorkersApiRef);
  const load = useCallback(async () => {
    const updates = await api.getPendingUpdates();
    return updates.flatMap((update): NeedsYouItem[] => {
      const failing = update.verification.rollup === 'fail';
      const ready = update.verification.rollup === 'pass';
      if (!failing && !ready) return [];
      return [
        {
          id: `internal-app-${update.app}-${update.pullRequestNumber}`,
          source: 'internal-apps',
          priority: failing ? 'your-move' : 'wrap-up',
          urgency: failing ? 'warning' : 'normal',
          label: failing ? 'App checks failing' : 'App update ready',
          meta: `${update.app} · updated ${timeAgo(update.updatedAt)}`,
          title: update.displayTitle || update.app,
          action: {
            label: failing ? 'Open' : 'Publish',
            href: getVibeCodedAppPath(update.app),
          },
          since: new Date(update.updatedAt).getTime(),
        },
      ];
    });
  }, [api]);
  return toState(enabled, useAsyncValue(enabled ? load : none));
};

// ---- Deployments -----------------------------------------------------------

/**
 * Deploys of the user's recently merged pull requests that failed or got
 * stuck (see deployItems). Needs a GitHub sign-in to find those pull
 * requests; the deployments backend is asked only for their repositories.
 */
export const useDeployItems = (
  enabled: boolean,
  signedIn: boolean,
): NeedsYouSourceState => {
  const scmAuth = useApi(scmAuthApiRef);
  const deployments = useApi(deploymentsApiRef);
  const active = enabled && signedIn;
  const load = useCallback(async () => {
    const now = Date.now();
    const merged = await fetchMergedPullRequests(scmAuth, now);
    if (merged.length === 0) return [];
    const repos = [...new Set(merged.map(pr => pr.repository.name))];
    const { apps } = await deployments.getApps({ repos: repos.join(',') });
    return deployItems(merged, apps, now);
  }, [scmAuth, deployments]);
  return toState(active, useAsyncValue(active ? load : none));
};
